import React, { useState, useEffect } from "react";
import {
	Modal,
	Box,
	Typography,
	Link,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Switch,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useAppContext } from "../../context/AppContext";
import { cookiePreferenceItems } from "../../constants";
import styles from "./CookiePreference.module.scss";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	maxHeight: "90%",
	overflowY: "auto",
	transform: "translate(-50%, -50%)",
	width: "40%",
	bgcolor: "background.paper",
	borderRadius: "10px",
	boxShadow: 24,
	p: 2,
	zIndex: 999999,
	pointerEvents: "auto", // Add this
};

const CookiePreferenceModal = () => {
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const {
		setShowCookiePreferenceModal,
		showCookiePreferenceModal,
		saveCookiePreference,
		cookiePreference,
		setCookiePreference,
		cookiePreferenceSet,
	} = useAppContext();

	// Ensure modal content is clickable when opened
	useEffect(() => {
		if (showCookiePreferenceModal) {
			// Temporarily restore pointer events for modal interaction
			document.body.style.pointerEvents = 'auto';
		}
		
		return () => {
			// Restore blocking if preferences not set
			if (!cookiePreferenceSet) {
				document.body.style.pointerEvents = 'none';
			}
		};
	}, [showCookiePreferenceModal, cookiePreferenceSet]);

	const handleToggleAccordion = (e, accordionId) => {
		e.stopPropagation();
		if (expandedAccordion == accordionId) {
			setExpandedAccordion(null);
		} else {
			setExpandedAccordion(accordionId);
		}
	};

	const handleToggleSwitch = (e, accordionId) => {
		e.stopPropagation();
		setCookiePreference({
			...cookiePreference,
			[accordionId]: !cookiePreference[accordionId],
		});
	};

	const onSaveCookiePreference = () => {
		setShowCookiePreferenceModal(false);
		saveCookiePreference(cookiePreference);
		
		// Restore interaction after saving
		document.body.style.overflow = '';
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.left = '';
		document.body.style.right = '';
		document.body.style.pointerEvents = '';
	};

	const handleClose = () => {
		// Only allow closing if preferences are already set
		if (cookiePreferenceSet) {
			setShowCookiePreferenceModal(false);
		}
		// If no preferences set, do nothing (can't close)
	};

	return (
		<Modal
			open={showCookiePreferenceModal}
			onClose={handleClose}
			disableEscapeKeyDown={!cookiePreferenceSet}
			style={{ 
				zIndex: 999999,
				pointerEvents: "auto" // Add this
			}}
			BackdropProps={{
				style: { 
					zIndex: 999998,
					pointerEvents: "auto" // Add this
				}
			}}
		>
			<Box sx={style}>
				<Box display={"flex"} flexDirection={"column"} gap="1em" style={{ pointerEvents: "auto" }}>
					<Typography fontSize="20px" fontWeight="bold">
						About your Privacy and Cookies Settings
					</Typography>
					<Typography variant="body2">
						Cookies are important to the proper functioning of a site and to
						provide you with a more personalized user experience. We want you to
						make informed choices; click on each category below to learn more
						about why we use cookies and then manage your own preferences. For
						more detailed information, please see our{" "}
						<Link
							href="https://www.telus.com/en/about/privacy/cookies"
							target="_blank"
							rel="noopener"
							underline="hover"
						>
							Cookie Statement
						</Link>
					</Typography>
					<Box style={{ pointerEvents: "auto" }}>
						{cookiePreferenceItems.map((item) => (
							<Accordion
								key={item.id}
								expanded={expandedAccordion == item.id}
								onChange={(e) => handleToggleAccordion(e, item.id)}
								style={{ pointerEvents: "auto" }}
							>
								<AccordionSummary
									expandIcon={
										<KeyboardArrowDownOutlinedIcon sx={{ color: "#66cc00" }} />
									}
									sx={{ flexDirection: "row-reverse", gap: "1em" }}
									style={{ pointerEvents: "auto" }}
								>
									<Box
										display="flex"
										alignItems="center"
										width="100%"
										justifyContent="space-between"
										style={{ pointerEvents: "auto" }}
									>
										<Typography variant="subtitle2">{item.title}</Typography>
										<Switch
											aria-label="essential-switch"
											checked={
												cookiePreference
													? cookiePreference[item.id]
													: item.defaultChecked
											}
											disabled={item.disabled}
											onClick={(e) => e.stopPropagation()}
											onChange={(e) => handleToggleSwitch(e, item.id)}
											style={{ pointerEvents: "auto" }}
										/>
									</Box>
								</AccordionSummary>
								<AccordionDetails style={{ pointerEvents: "auto" }}>
									<Typography variant="body2">{item.description}</Typography>
								</AccordionDetails>
							</Accordion>
						))}
					</Box>
					<Box
						display="flex"
						justifyContent="flex-end"
						justifyItems="flex-end"
						gap="1em"
						width="100%"
						style={{ pointerEvents: "auto" }}
					>
						{cookiePreferenceSet && (
							<button
								onClick={() => setShowCookiePreferenceModal(false)}
								className={styles.buttonClose}
								style={{ pointerEvents: "auto" }}
							>
								Close
							</button>
						)}
						<button
							onClick={() => onSaveCookiePreference()}
							className={styles.buttonSave}
							style={{ pointerEvents: "auto" }}
						>
							Save Preferences
						</button>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
};

export default CookiePreferenceModal;
